/* eslint-disable max-len */
import React, { useEffect, useMemo, useState } from 'react';
import { Grid } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { exportToExcelData } from '../../Common/PageHeader/helper';
import ComponentPageHeader from '../../Common/PageHeader/ComponentPageHeader';
import DataTable from '../../Common/DataTable';
import { ReportsInformationRes, ReportsInformationListingInterface } from './utils/reportsTypes';
import { RootState } from '../../redux/rootState';
import { setLoader } from '../../redux/loaderSlice';
import { getVesselInformationStart, onCopyReportsStart, setCheckedReportIds } from '../VesselInformation/redux/sliceVesselInformation';
import { ReportsInformationTableColumns } from './utils/reportsHelper';
import { AnalystData, AnalystDataPayload } from '../VesselInformation/utils/type';
import { convertDateFormat } from '../../utils/CommonFunctions';
import { showErrorToaster } from '../../Common/ComponentToast/ComponentSuccessToasts';
import {
  resetPagination,
  setPageNumber, setPageSize, setSearchParam, setSortColumn, setSortDirection,
} from '../taiReportingSystem/redux/sliceReportWorkspace';

function ReportsInformation() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // const [page, setPage] = useState(1);
  // const [rowsPerPage, setRowsPerPage] = useState(10);
  // const [searchQuery, setSearchQuery] = useState('');
  // const [sortCol, setSortCol] = useState('');
  // const [sortDir, setSortDir] = useState('asc');
  const {
    pageNumber, pageSize, searchParam, sortColumn, sortDirection,
  } = useSelector((state: RootState) => state.reportWorkspaceReducer);
  const { historyStack } = useSelector((state:RootState) => state.masterDataReducer);
  const {
    vesselInformation, totalVesselInfo, isLoading, checkedReportIds, isCopyLoading,
  } = useSelector((state:RootState) => state.VesselInformationReducer);
  const [data, setData] = useState<ReportsInformationListingInterface[]>([]);

  const handleData = () => {
    const payload = {
      page: pageNumber,
      searchQuery: searchParam,
      rowsPerPage: pageSize,
      sortCol: sortColumn,
      sortDir: sortDirection,
    };
    dispatch(getVesselInformationStart(payload));
  };

  useEffect(() => {
    if (isCopyLoading || isLoading) {
      dispatch(setLoader(true));
    } else {
      dispatch(setLoader(false));
    }
  }, [isLoading, isCopyLoading]);

  const tableData: ReportsInformationListingInterface[] = useMemo(() => {
    if (vesselInformation?.length > 0) {
      const transformedData = vesselInformation?.map((ele, index) => ({
        sn: index,
        action: 'eye',
        jobOrder: ele.JobOrder,
        vesselName: ele.VesselName,
        serialNumber: ele.SerialNumber,
        siteName: ele.SiteName,
        model: ele.Model,
        analyst: ele.Analyst,
        inspectionDate: convertDateFormat(ele.InspectionDate),
        id: `${ele.JobOrder}_${ele.VesselId}_${ele.AnalystId}_${ele.IsMultiOffset ? 'Y' : 'N'}`,
        isChecked: checkedReportIds.includes(`${ele.JobOrder}_${ele.VesselId}_${ele.AnalystId}_${ele.IsMultiOffset ? 'Y' : 'N'}`),
        isMultiOffset: ele.IsMultiOffset ? 'Yes' : 'No',
      }));
      return transformedData;
    }
    return [];
  }, [vesselInformation, checkedReportIds]);

  useEffect(() => {
    if (vesselInformation) {
      setData(tableData);
    }
  }, [vesselInformation]);

  const handleClickViews = (id: string) => {
    const record = vesselInformation.find((ele:ReportsInformationRes) => `${ele.JobOrder}_${ele.VesselId}_${ele.AnalystId}_${ele.IsMultiOffset ? 'Y' : 'N'}` === id);
    if (record) {
      navigate(`${record.JobOrder}/${record.VesselId}/${record.AnalystId}`);
    }
  };

  const handleCheckBoxWithKey = (id: string) => {
    const jobIds: string[] = [...checkedReportIds];
    const updatedData = data?.map((element: ReportsInformationListingInterface) => {
      // if (element?.id === id) {
      //   return {
      //     ...element,
      //     isChecked: !element?.isChecked,
      //   };
      // }
      // return element;
      if (element?.id === id) {
        if (!element.isChecked) {
          jobIds.push(element.id);
        } else {
          const ind = jobIds.findIndex((ele) => ele === id);
          jobIds.splice(ind, 1);
        }
        return {
          ...element,
          isChecked: !element?.isChecked,
        };
      }
      return element;
    });
    setData(updatedData);
    dispatch(setCheckedReportIds(jobIds));
  };

  useEffect(() => {
    handleData();
  }, [pageNumber, pageSize, searchParam, sortColumn, sortDirection]);
  useEffect(() => () => {
    dispatch(setCheckedReportIds([]));
  }, []);

  const excelData = useMemo(() => vesselInformation?.map((item) => {
    const transformedDataItems = {
      JobOrder: item.JobOrder,
      VesselName: item.VesselName,
      SerialNumber: item.SerialNumber,
      JobSite: item.SiteName,
      ModelNumber: item.Model,
      Analyst: item.Analyst,
      InspectionDate: convertDateFormat(item.InspectionDate),
      'IsMultiOffset?': item.IsMultiOffset ? 'Yes' : 'No',
    };
    return transformedDataItems;
  }), [vesselInformation]);

  const downloadExcel = () => {
    exportToExcelData(
      {
        data: excelData,
      },
      'REPORTS INFORMATION',
    );
  };

  const handleButtonClick = () => {
    // const jobIds: string[] = [];
    // data?.forEach((element) => {
    //   if (element?.isChecked) {
    //     jobIds.push(element?.id);
    //   }
    // });
    if (checkedReportIds?.length <= 5) {
      const payload = {
        selectedIds: checkedReportIds,
      };
      const payLoad: AnalystDataPayload = {
        data: payload?.selectedIds?.map((item): AnalystData => {
          const parts = item.split('_');
          return {
            JobOrder: `${parts[0]}`,
            VesselId: parts[1],
            AnalystId: parts[2],
            IsMultiOffset: parts[3] === 'Y',
          };
        }),
      };
      dispatch(onCopyReportsStart({ payLoad, handleData: () => { dispatch(setCheckedReportIds([])); handleData(); } }));
    } else {
      showErrorToaster(`You have selected ${checkedReportIds?.length} reports. A maximum of 5 reports can be downloaded at a time.`);
    }
  };

  useEffect(() => {
    if (historyStack) {
      const finderString = 'reports/';
      const last = historyStack[historyStack.length - 1];
      const sLast = historyStack[historyStack.length - 2];
      if (!(last?.includes(finderString) || (sLast?.includes(finderString) && last?.includes('reports')))) {
        dispatch(resetPagination());
      }
    }
  }, [historyStack]);

  // const jobSelected = useMemo(() => data?.every((ele) => !ele?.isChecked), [data]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={12} md={12}>
        <ComponentPageHeader subHeading="REPORTS" downloadExcel={downloadExcel} />
      </Grid>
      <Grid item xs={12} sm={12} md={12} className="dataTableCustomMargin">
        <DataTable<ReportsInformationListingInterface>
          data={data?.length > 0 ? data : []}
          columns={ReportsInformationTableColumns}
          totalRecords={totalVesselInfo}
          page={pageNumber}
          setPage={(e) => { dispatch(setPageNumber(e)); }}
          buttonText="Queue for Sync"
          rowsPerPage={pageSize}
          setRowsPerPage={(e) => { dispatch(setPageSize(e)); }}
          searchQuery={searchParam}
          setSearchQuery={(e) => { dispatch(setSearchParam(e)); dispatch(setPageNumber(1)); }}
          sortCol={sortColumn}
          setSortCol={(e) => { dispatch(setSortColumn(e)); dispatch(setPageNumber(1)); }}
          sortDir={sortDirection}
          setSortDir={(e) => { dispatch(setSortDirection(e)); dispatch(setPageNumber(1)); }}
          handleData={handleData}
          handleViews={handleClickViews}
          checkBoxWithKey={['sn']}
          handleCheckBoxWithKey={handleCheckBoxWithKey}
          handleButtonClick={handleButtonClick}
          textButtonDisabled={Boolean(checkedReportIds.length === 0)}
          handleDataFromComponent
        />
      </Grid>
    </Grid>
  );
}

export default ReportsInformation;
